/* eslint-disable max-len */
/* eslint-disable import/no-cycle */
import { TimeSlot } from '@brands/Dashboard/PatientForms/PatientForm/utils/ProviderAvailabilityCard/Components/TimeSlotComponent';
import { Meta } from '@brands/services/communication/types/Meta';
import { uniqBy } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { ListAvailableProviderParams, listAvailableProviders } from '../services/identity/listAvailableProviders';
import { UserProfile } from '../services/identity/types/UserProfile';
import { isValidDateFormat } from '../Utils/validation';

interface IUseUsers {
  providers: IListAvailableProvider[];
  dataLoading: boolean;
  error: unknown;
  loadNextPage: () => Promise<void>;
  refreshProviders: () => void;
}

export interface IListAvailableProvider {
  created_at: string;
  id: number;
  sync_time_slots: string[];
  sync_time_intervals: TimeSlot[];
  async_time_slots: string[];
  async_time_intervals: TimeSlot[];
  updated_at: string;
  user: UserProfile;
  in_network?: boolean;
}

export default function useAvailableProviders(
  user_type: string,
  params: Pick<
    ListAvailableProviderParams,
    | 'sort_by'
    | 'sort_direction'
    | 'limit'
    | 'date'
    | 'tz'
    | 'carrier_id'
    | 'language'
    | 'state'
    | 'license_states'
    | 'patient_id'
    | 'out_of_network'
    | 'case_type'
    | 'lookahead_days'
  >,
): IUseUsers {
  const [providers, setProviders] = useState<IListAvailableProvider[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const [firstPageMeta, setFirstPageMeta] = useState<Meta | null>(null);
  const [lastPageMeta, setLastPageMeta] = useState<Meta | null>(null);
  const requestIdRef = useRef(0);

  const loadNextPage = async (): Promise<void> => {
    if (!lastPageMeta?.has_next_page) {
      return;
    }
    const { meta, nodes } = await listAvailableProviders(user_type, {
      cursor: lastPageMeta.end_cursor,
      cursor_direction: 'next',
      ...params,
    });
    setLastPageMeta(meta);
    if (!firstPageMeta) {
      setFirstPageMeta(meta);
    }
    setProviders((prevProviders) => uniqBy([...prevProviders, ...nodes], 'id'));
  };

  useEffect(() => {
    let canceled = false;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (params.date !== '' && isValidDateFormat(params.date)) {
      setDataLoading(true);
      setError(null);
      listAvailableProviders(user_type, params)
        .then((usersList) => {
          if (!canceled && requestIdRef.current === requestId) {
            setProviders(usersList.nodes);
            setFirstPageMeta(usersList.meta);
            setLastPageMeta(usersList.meta);
          }
        })
        .catch((err) => {
          // Surface failures: log for ops visibility and expose via `error` so callers can react.
          // eslint-disable-next-line no-console
          console.error('[useAvailableProvidersNew] listAvailableProviders failed', err);
          if (!canceled && requestIdRef.current === requestId) {
            setError(err);
          }
        })
        .finally(() => {
          if (!canceled && requestIdRef.current === requestId) {
            setDataLoading(false);
          }
        });
    } else {
      setDataLoading(false);
    }

    return (): void => {
      canceled = true;
    };
  }, [params, user_type]);

  const refreshProviders = (): void => {
    if (params.date !== '') {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setDataLoading(true);
      setError(null);
      listAvailableProviders(user_type, params)
        .then((usersList) => {
          if (requestIdRef.current === requestId) {
            setProviders(usersList.nodes);
            setFirstPageMeta(usersList.meta);
            setLastPageMeta(usersList.meta);
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[useAvailableProvidersNew] refreshProviders failed', err);
          if (requestIdRef.current === requestId) {
            setError(err);
          }
        })
        .finally(() => {
          if (requestIdRef.current === requestId) {
            setDataLoading(false);
          }
        });
    }
  };

  return {
    providers,
    dataLoading,
    error,
    loadNextPage,
    refreshProviders,
  };
}
